import { useEffect, useRef } from 'react';

import { useNewRoomStore } from 'shared/store/room-store';
import { useStreamStore } from 'shared/store/stream-store';
import { isMocked } from 'shared/utils/isMocked';

import SceneManager from './core/SceneManager';
import Loading from './scenes/Loading';
import Main from './scenes/Main';

const PixiComponent = () => {
  const pixiContainer = useRef<HTMLDivElement | null>(null);
  const sceneManagerRef = useRef<SceneManager | null>(null);

  const { manageQueue, currentPhase, currentRound, queue } = useStreamStore();
  const { room, rounds } = useNewRoomStore();

  useEffect(() => {
    const sceneManager = new SceneManager();

    const initializeApp = async () => {
      const canvas = document.createElement('canvas');
      canvas.style.width = '100%';
      canvas.style.height = '100%';

      if (pixiContainer.current) {
        while (pixiContainer.current.firstChild) {
          pixiContainer.current.removeChild(pixiContainer.current.firstChild);
        }
        pixiContainer.current.appendChild(canvas);
      }

      const { width, height } = pixiContainer.current!.getBoundingClientRect();

      await sceneManager.init({
        canvas,
        resizeTo: pixiContainer.current as HTMLElement,
        powerPreference: 'high-performance',
        resolution: window.devicePixelRatio || 1,
        background: '#000000',
        backgroundAlpha: 0,
        clearBeforeRender: true,
        width,
        height,
        antialias: false,
        hello: true,
      });

      await sceneManager.switchScene(Loading);
      await sceneManager.switchScene(Main);

      sceneManagerRef.current = sceneManager;
      sceneManager.triggerResize(width, height);

      // 👇 if visual test mode — render scene as <img>
      if (isMocked) {
        // wait for a moment to ensure rendering is complete
        setTimeout(() => {
          try {
            const dataUrl = canvas.toDataURL('image/png');
            const image = new Image();
            image.src = dataUrl;
            image.style.position = 'absolute';
            image.style.top = '0';
            image.style.left = '0';
            image.style.width = '100%';
            image.style.height = '100%';
            image.style.pointerEvents = 'none';
            pixiContainer.current!.appendChild(image);
          } catch (err) {
            console.error('Failed to capture canvas as image', err);
          }
        }, 5000);
        return;
      }
    };

    initializeApp().catch((error) => {
      console.error('Failed to initialize PixiJS app:', error);
    });

    return () => {
      if (sceneManagerRef.current && sceneManagerRef.current.app) {
        sceneManagerRef.current.app.destroy();
        sceneManagerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    manageQueue({ phase: currentPhase, round: currentRound });
    if (sceneManagerRef.current && !isMocked) {
      const mainScene = sceneManagerRef.current.currentScene;
      if (mainScene instanceof Main) {
        mainScene.updatePhase(currentPhase, currentRound, rounds, room, queue);
      }
    }
  }, [currentPhase, currentRound, isMocked]);

  return <div ref={pixiContainer} style={{ width: '100%', height: '100%' }} />;
};

export default PixiComponent;
